import React, { useEffect } from "react";
import { Modal, Input, Row, Col, Button, message } from "antd";
import Refresh from "../../assets/images/refresh.svg";
import CancelArrow from "../../assets/images/cancelArrow.svg";
import usePOSConfigStore from "../../stores/usePOSConfigStore";
import useAdvanceOrderStore from "../../stores/useAdvanceOrderStore";
import { KEYPAD_NO_FOCUS } from "../keypadNoFocus";
import { isFractionBlocked } from "../hooks/productPopupUtils";
import ScaleStatusLine from "./ScaleStatusLine";

/**
 * IntegratedWeightModal — shared weight/quantity entry modal for products
 * WITHOUT alternate UOM (those with alternate UOM use WeightUomPopup instead).
 *
 * Two layout variants controlled by posConfig.predefined_weight_buttons:
 *   - "Y" => wide (50%) layout with unit panel + optional quick-select
 *   - else => narrow (30%) layout with just numpad
 *
 * Props:
 *   open          - boolean, controls modal visibility
 *   product       - the product object (must have .name, .imageurl, .weight, .sunitprice, etc.)
 *   onTotalQty    - (key: string) => void — numpad button handler ("1"-"9","0","00",".","x","clear")
 *   onInputChange - (value: string) => void — free-text input change handler
 *   onConfirm     - () => void — confirm/enter action
 *   onCancel      - () => void — cancel action
 *   inputRef      - React ref for the quantity Input
 *   onRefresh     - optional () => void — scale refresh button handler (hidden when absent)
 *   unit          - optional string ("kg"|"g") — selected unit for unit selector panel
 *   onUnitChange  - optional (unit: string) => void — called when unit button clicked
 *   frequentWeights - optional number[] — quick-select weight presets
 *   onFrequentWeight - optional (weight: number) => void — called when preset clicked
 *   zIndex        - optional number (default 2500)
 *   strictQty     - optional boolean — when true, confirm is blocked if weight <= 0
 *   scaleStatus   - optional { title, action } (or string) — why the scale gives no weight (BUG-2568), shown above the input
 *   scaleRecovered - optional boolean — the scale just came back after a message: the weight shows green briefly
 */
const IntegratedWeightModal = ({
  open,
  product,
  onTotalQty,
  onInputChange,
  onConfirm,
  onCancel,
  inputRef,
  onRefresh,
  unit,
  onUnitChange,
  frequentWeights,
  onFrequentWeight,
  zIndex = 2500,
  strictQty = false,
  scaleStatus = null,
  scaleRecovered = false,
}) => {
  const weightColor = scaleRecovered ? "#389e0d" : "#2F3856";
  const posConfig = usePOSConfigStore((s) => s.posConfig);
  const isAdvanceOrderEnabled = useAdvanceOrderStore.getState().isAdvanceOrderEnabled;
  const isPreOrderPage = window.location.pathname.includes("/preorder");

  const isPredefined = posConfig?.predefined_weight_buttons === "Y";
  const hasUnitSelector = !!onUnitChange;
  const isDisabled = !isAdvanceOrderEnabled && !isPreOrderPage && product?.restrictPosQtyEdit === "Y";
  const isDecimal = product?.uomData?.[0]?.decimal === "Y";
  const weightValue = product?.weight || product?.rawWeight || "";
  const isZeroWeight = Number(product?.weight || 0) <= 0;
  // BUG-2451: handleKeyDown below only stops a physical "." key. The on-screen "." button, a
  // pasted value, the quick-select presets and the scale all bypassed it, so a non-decimal UOM
  // (Numbers/Pieces) product could be confirmed at a fraction -- the cart stores that as qty 0
  // but prices it at the fraction, and the order is rejected on sync (header total != lines).
  const fractionBlocked = isFractionBlocked(product);
  const hasBlockedFraction = fractionBlocked && !Number.isInteger(Number(product?.weight || 0));

  const warnWholeQtyOnly = () => message.warning({ content: "Only whole quantities are allowed for this product.", key: "whole-qty-only" });

  const handleInput = (value) => {
    if (fractionBlocked && value.includes(".")) {
      warnWholeQtyOnly();
      onInputChange(value.replace(/\./g, ""));
      return;
    }
    onInputChange(value);
  };

  const handleDecimalPoint = () => {
    if (fractionBlocked) {
      warnWholeQtyOnly();
      return;
    }
    onTotalQty(".");
  };

  const handleConfirm = () => {
    if (strictQty && isZeroWeight) return;
    if (hasBlockedFraction) {
      warnWholeQtyOnly();
      return;
    }
    onConfirm();
  };

  // Focus + select: initial focus on open, then retain focus while modal is open
  useEffect(() => {
    if (!open || isDisabled) return;

    const focusInput = () => {
      if (!inputRef?.current) return;
      const active = document.activeElement;
      const modalEl = inputRef.current?.input?.closest?.(".ant-modal") || inputRef.current?.closest?.(".ant-modal");
      const isFocusInsideModal = modalEl && modalEl.contains(active);
      if (!isFocusInsideModal) {
        inputRef.current?.focus?.();
        inputRef.current?.select?.();
      }
    };

    setTimeout(focusInput, 50);
    const interval = setInterval(focusInput, 2000);
    return () => clearInterval(interval);
  }, [open, inputRef, isDisabled]);

  // BUG-2354: antd Input already calls onPressEnter (handlePressEnter) before this
  // handler on Enter, so confirming here too fired onConfirm twice per keypress.
  // Enter is only swallowed here; handlePressEnter is the single confirm path.
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.stopPropagation();
      e.preventDefault();
    } else if (e.key === "Backspace") {
      e.preventDefault();
      onTotalQty("x");
    } else if (!isDecimal && !/[\d]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
      e.preventDefault();
    } else if (isDecimal && !/[\d.]/.test(e.key) && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(e.key)) {
      e.preventDefault();
    }
  };

  const handlePressEnter = () => {
    handleConfirm();
  };

  // A scale notice adds a few lines above the input: shorter keys keep Cancel / Confirm on screen.
  const keyH = scaleStatus ? "8vh" : "10vh";
  const keySize = scaleStatus ? { height: keyH } : undefined;

  // Shared numpad grid
  const renderNumpad = () => (
    <>
      {["1", "2", "3"].map((n) => (
        <Col span={6} key={n}>
          <button
            {...KEYPAD_NO_FOCUS} className="amt-dial-btn-production"
            onClick={() => onTotalQty(n)}
            style={isPredefined ? { width: "100%", height: keyH, border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1.2rem" } : keySize}
          >
            {n}
          </button>
        </Col>
      ))}
      <Col span={6}>
<button
  {...KEYPAD_NO_FOCUS} className="amt-dial-btn-production"
  onClick={() => onTotalQty("x")}
  style={isPredefined ? {
    width: "100%",
    height: keyH,
    border: "1px solid #d9d9d9",
    borderRadius: "5px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  } : { display: "flex", justifyContent: "center", alignItems: "center", ...keySize }}
>
  <img src={CancelArrow} alt="back space" width={25} height={15} />
</button>
      </Col>
      {["4", "5", "6"].map((n) => (
        <Col span={6} key={n}>
          <button
            {...KEYPAD_NO_FOCUS} className="amt-dial-btn-production"
            onClick={() => onTotalQty(n)}
            style={isPredefined ? { width: "100%", height: keyH, border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1.2rem" } : keySize}
          >
            {n}
          </button>
        </Col>
      ))}
      <Col span={6}>
        <button
          {...KEYPAD_NO_FOCUS} className="amt-dial-btn-production"
          onClick={() => onTotalQty("clear")}
          style={isPredefined ? { width: "100%", height: keyH, border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1rem" } : keySize}
        >
          Clear
        </button>
      </Col>
      <Col span={18}>
        <Row gutter={[8, 6]}>
          {["7", "8", "9"].map((n) => (
            <Col span={8} key={n}>
              <button
                {...KEYPAD_NO_FOCUS} className="amt-dial-btn-production"
                onClick={() => onTotalQty(n)}
                style={isPredefined ? { width: "100%", height: keyH, border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1.2rem" } : keySize}
              >
                {n}
              </button>
            </Col>
          ))}
          <Col span={8}>
            <button
              {...KEYPAD_NO_FOCUS} className="amt-dial-btn-production"
              onClick={() => onTotalQty("0")}
              style={isPredefined ? { width: "100%", height: keyH, border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1.2rem" } : keySize}
            >
              0
            </button>
          </Col>
          <Col span={8}>
            <button
              {...KEYPAD_NO_FOCUS} className="amt-dial-btn-production"
              onClick={() => onTotalQty("00")}
              style={isPredefined ? { width: "100%", height: keyH, border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1rem" } : keySize}
            >
              00
            </button>
          </Col>
          <Col span={8}>
            <button
              {...KEYPAD_NO_FOCUS} className="amt-dial-btn-production"
              onClick={handleDecimalPoint}
              style={isPredefined ? { width: "100%", height: keyH, border: "1px solid #d9d9d9", borderRadius: "5px", fontSize: "1.2rem" } : keySize}
            >
              .
            </button>
          </Col>
        </Row>
      </Col>
      <Col span={6}>
        <button
          {...KEYPAD_NO_FOCUS} className="amt-dial-btn-production-ent"
          onClick={handleConfirm}
          disabled={strictQty && isZeroWeight}
          style={isPredefined
            ? { width: "100%", height: scaleStatus ? "16.5vh" : "20.5vh", backgroundColor: strictQty && isZeroWeight ? "#a0a0a0" : "#2F3856", color: "#fff", borderRadius: "5px", fontSize: "1.2rem", cursor: strictQty && isZeroWeight ? "not-allowed" : "pointer" }
            : { ...(scaleStatus ? { height: "17.1vh" } : {}), ...(strictQty && isZeroWeight ? { opacity: 0.5, cursor: "not-allowed" } : {}) }}
        >
          Enter
        </button>
      </Col>
    </>
  );

  // --- Predefined (wide) layout ---
  const renderPredefined = () => (
    <Modal
      open={open}
      getContainer={false}
      zIndex={zIndex}
      closable={false}
      keyboard={false}
      footer={null}
      width="50%"
      centered
      styles={{ body: { padding: 10, backgroundColor: "#F3F4F9", height: "80vh" } }}
      afterOpenChange={(isOpen) => {
        if (isOpen && inputRef?.current && !isDisabled) {
          setTimeout(() => {
            inputRef.current?.focus?.();
            inputRef.current?.select?.();
          }, 50);
        }
      }}
    >
      {product && (
        <>
          <Row>
            <Col span={24} style={{ paddingBottom: "1vh" }}>
              <img src={product.imageurl} style={{ height: "4.5vh", paddingRight: "1vh" }} />
              <span style={{ fontWeight: 500, fontSize: "1.2rem" }}>{product.name}</span>
            </Col>
            <Col span={12} style={{ textAlign: "left", paddingLeft: "0.5vw", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Sale Price: <span style={{ fontWeight: 500, fontSize: "0.9rem" }}>{product.sunitprice}</span></span>
              {posConfig?.showStoreSpecificStock === "Y" && (
                <span>Stock: <span style={{ fontWeight: 500, fontSize: "0.9rem" }}>{product?.stock ?? 0}</span></span>
              )}
            </Col>
            <Col span={12} style={{ textAlign: "right" }}>
              {onRefresh ? (
                <img src={Refresh} style={{ cursor: "pointer" }} onClick={() => onRefresh(product)} />
              ) : (
                <img src={Refresh} style={{ cursor: "pointer" }} />
              )}
            </Col>
            {scaleStatus && (
              <Col span={24} style={{ padding: "1vh 3.5% 0 3.5%" }}>
                <ScaleStatusLine status={scaleStatus} />
              </Col>
            )}
          </Row>
          <Row style={{ padding: "1vh 3.5% 0 3.5%" }} gutter={[16, 16]}>
            <Col span={16}>
              <Row gutter={[8, 6]}>
                <Col span={24} style={{ paddingBottom: "1vh" }}>
                  <Input
                    className="productSearch sm-payment-search totalQtyInput"
                    style={{ borderRadius: "5px", height: "8.3vh", textAlign: "right", fontWeight: "500", fontSize: "2.5em", borderColor: "#fff", color: weightColor, fontFamily: "Inter" }}
                    data-scale-recovered={scaleRecovered ? "Y" : undefined}
                    value={product.weight ? `${product.weight}` : ""}
                    ref={inputRef}
                    onChange={(e) => handleInput(e.target.value.replace(/[^0-9.]/g, ""))}
                    disabled={isDisabled}
                    readOnly={!product || Object.keys(product).length === 0}
                    placeholder="Enter Quantity"
                    onPressEnter={handlePressEnter}
                    onKeyDown={handleKeyDown}
                  />
                </Col>
                {renderNumpad()}
              </Row>
            </Col>
            <Col span={8}>
              <Row gutter={[8, 8]}>
                <Col span={24}>
                  <div style={{ backgroundColor: "#fff", padding: "1vh", borderRadius: "5px", border: "1px solid #d9d9d9" }}>
                    <h4 style={{ fontSize: "0.9rem", fontWeight: 600, margin: "0 0 0.5vh 0" }}>Unit</h4>
                    {hasUnitSelector ? (
                      <>
                        <Button
                          {...KEYPAD_NO_FOCUS}
                          style={{
                            width: "100%", height: "5vh", marginBottom: "0.5vh",
                            backgroundColor: unit === "kg" ? "#2F3856" : "#fff",
                            color: unit === "kg" ? "#fff" : "#2F3856",
                            border: "1px solid #d9d9d9",
                          }}
                          onClick={() => { onUnitChange("kg"); onTotalQty("clear"); }}
                        >
                          Kilograms
                        </Button>
                        <Button
                          {...KEYPAD_NO_FOCUS}
                          style={{
                            width: "100%", height: "5vh", marginBottom: "0.5vh",
                            backgroundColor: unit === "g" ? "#2F3856" : "#fff",
                            color: unit === "g" ? "#fff" : "#2F3856",
                            border: "1px solid #d9d9d9",
                          }}
                          onClick={() => { onUnitChange("g"); onTotalQty("clear"); }}
                        >
                          Grams
                        </Button>
                      </>
                    ) : (
                      <div style={{ padding: "8px 0", fontSize: "0.9rem", fontWeight: 500 }}>
                        {product?.uomData?.[0]?.name || "Default"}
                      </div>
                    )}
                  </div>
                </Col>
                {frequentWeights && onFrequentWeight && (
                  <Col span={24}>
                    <div style={{ backgroundColor: "#fff", padding: "1vh", borderRadius: "5px", border: "1px solid #d9d9d9", maxHeight: "36vh", overflowY: "auto" }}>
                      <h4 style={{ fontSize: "0.9rem", fontWeight: 600, margin: "0 0 0.5vh 0" }}>Quick Select</h4>
                      <Row gutter={[8, 8]}>
                        {frequentWeights.map((w) => (
                          <Col span={12} key={w}>
                            <Button
                              {...KEYPAD_NO_FOCUS}
                              style={{ width: "100%", height: "4vh", fontSize: "0.85rem", border: "1px solid #d9d9d9" }}
                              onClick={() => onFrequentWeight(w)}
                            >
                              {w} {unit}
                            </Button>
                          </Col>
                        ))}
                      </Row>
                    </div>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
          <Row style={{ padding: "3% 3.5% 0 3.5%" }} gutter={[8, 6]}>
            <Col span={12}>
              <Button onClick={onCancel} style={{ width: "100%", height: "7vh", borderRadius: "5px", backgroundColor: "#fff", color: "#2F3856" }}>Cancel</Button>
            </Col>
            <Col span={12}>
              <Button
                onClick={handleConfirm}
                disabled={strictQty && isZeroWeight}
                style={{ width: "100%", height: "7vh", borderRadius: "5px", backgroundColor: strictQty && isZeroWeight ? "#a0a0a0" : "#2F3856", color: "#fff", cursor: strictQty && isZeroWeight ? "not-allowed" : "pointer" }}
              >
                Confirm
              </Button>
            </Col>
          </Row>
        </>
      )}
    </Modal>
  );

  // --- Simple (narrow) layout ---
  const renderSimple = () => (
    <Modal
      open={open}
      getContainer={false}
      zIndex={zIndex}
      closable={false}
      keyboard={false}
      footer={null}
      width="30%"
      centered
      styles={{ body: { padding: 10, backgroundColor: "#F3F4F9" } }}
      afterOpenChange={(isOpen) => {
        if (isOpen && inputRef?.current && !isDisabled) {
          setTimeout(() => {
            inputRef.current?.focus?.();
            inputRef.current?.select?.();
          }, 50);
        }
      }}
    >
      {product && (
        <>
          {/* The scale notice sits in the refresh row (left of the icon) instead of adding a row of its own */}
          <Row wrap={false} align="top" style={{ gap: "8px" }}>
            <Col flex="auto" style={{ textAlign: "left", paddingLeft: scaleStatus ? "3.5%" : "0.5vw", minWidth: 0 }}>
              {scaleStatus && <ScaleStatusLine status={scaleStatus} style={{ padding: "4px 8px", fontSize: "0.8rem", lineHeight: 1.3 }} />}
            </Col>
            <Col flex="none" style={{ textAlign: "right" }}>
              {onRefresh ? (
                <img src={Refresh} style={{ cursor: "pointer" }} onClick={() => onRefresh(product)} />
              ) : (
                <img src={Refresh} style={{ cursor: "pointer" }} />
              )}
            </Col>
          </Row>
          <Row style={{ padding: scaleStatus ? "1vh 3.5% 0 3.5%" : "0 3.5% 0 3.5%" }} gutter={[8, 6]}>
            <Col span={24} style={{ paddingBottom: "1vh" }}>
              <img src={product.imageurl} style={{ height: "4.5vh" }} />{" "}
              <span style={{ fontWeight: 500, fontSize: "1rem" }}>{product.name}</span>
            </Col>
            <Col span={24} style={{ paddingBottom: "1vh", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>Sale Price: <span style={{ fontWeight: 500, fontSize: "0.9rem" }}>{product.sunitprice}</span></span>
              {posConfig?.showStoreSpecificStock === "Y" && (
                <span>Stock: <span style={{ fontWeight: 500, fontSize: "0.9rem" }}>{product?.onhandQty ?? 0}</span></span>
              )}
            </Col>
          </Row>
          <Row style={{ padding: "0 3.5% 0 3.5%" }} gutter={[8, 6]}>
            <Col span={24} style={{ paddingBottom: "2vh" }}>
              <Input
                className="productSearch sm-payment-search totalQtyInput"
                style={{ borderRadius: "0.5vw", height: "8.3vh", textAlign: "right", fontWeight: "500", fontSize: "2.5em", borderColor: "#fff", color: weightColor, fontFamily: "Inter" }}
                data-scale-recovered={scaleRecovered ? "Y" : undefined}
                disabled={isDisabled}
                value={weightValue}
                ref={inputRef}
                onChange={(e) => handleInput(e.target.value)}
                readOnly={!product || Object.keys(product).length === 0}
                placeholder="Enter Quantity"
                onPressEnter={handlePressEnter}
                onKeyDown={handleKeyDown}
              />
            </Col>
            {renderNumpad()}
          </Row>
          <Row style={{ padding: "3% 3.5% 0 3.5%" }} gutter={[8, 6]}>
            <Col span={12}>
              <Button onClick={onCancel} style={{ width: "100%", height: "7vh", borderRadius: "5px", backgroundColor: "#fff", color: "#2F3856" }}>Cancel</Button>
            </Col>
            <Col span={12}>
              <Button
                onClick={handleConfirm}
                disabled={strictQty && isZeroWeight}
                style={{ width: "100%", height: "7vh", borderRadius: "5px", backgroundColor: strictQty && isZeroWeight ? "#a0a0a0" : "#2F3856", color: "#fff", cursor: strictQty && isZeroWeight ? "not-allowed" : "pointer" }}
              >
                Confirm
              </Button>
            </Col>
          </Row>
        </>
      )}
    </Modal>
  );

  return isPredefined ? renderPredefined() : renderSimple();
};

export default IntegratedWeightModal;
